import { pooledPartnerPoints } from '@/config/chain/points'
import type { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import type { UsePointsDataResult } from '@/domain/points/pointsData/usePointsData'
import type { BorrowPair } from './useBorrowPairs'

const pooledPartnerPointsByAddress = Object.fromEntries(
  Object.entries(pooledPartnerPoints).map(([k, v]) => [k.toLowerCase(), v]),
)

interface PairWithRouteType extends BorrowPair {
  routeType?: 'euler' | 'pooled'
}

interface PairPointsInfo {
  hasHypurrPoints: boolean
  partnerPoints: IsolatedPartnerPointsPerPair[]
}

export function getPairPointsInfo(
  pair: PairWithRouteType,
  getByAddress: UsePointsDataResult['getByAddress'],
): PairPointsInfo {
  const pointsType = pair.routeType === 'pooled' ? 'pooled' : 'euler'

  const hasHypurrPoints =
    !!getByAddress({ type: pointsType, action: 'borrow', address: pair.liability })?.multiplier ||
    !!getByAddress({ type: pointsType, action: 'deposit', address: pair.collateral })?.multiplier

  const partnerPoints = (() => {
    if (pair.routeType === 'pooled') {
      const collateralPoints = (pooledPartnerPointsByAddress[pair.collateral.toLowerCase()]?.points ?? []).filter(
        (p) => p.deposit,
      )
      const liabilityPoints = (pooledPartnerPointsByAddress[pair.liability.toLowerCase()]?.points ?? []).filter(
        (p) => p.borrow,
      )
      const seen = new Set<string>()
      return [...collateralPoints, ...liabilityPoints].filter((p) => {
        if (seen.has(p.title)) return false
        seen.add(p.title)
        return true
      })
    }
    return pair.partnerPoints?.filter((p) => p.borrow || p.deposit || p.collateral) ?? []
  })()

  return {
    hasHypurrPoints,
    partnerPoints: partnerPoints as IsolatedPartnerPointsPerPair[],
  }
}
